import autobind from "autobind-decorator";
import dayjs from "dayjs";
import _ from "lodash";
import { connect } from "react-redux";

import Sentry from "@similarweb/sentry-logger";
import type { ISidebarListItem } from "@similarweb/ui-components";

import { durationFilterDSSelected, webSourceFilterDSSelected } from "app/actions/usersFiltersSelectionActions";
import { preparePresets } from "app/components/dashboard/widget-wizard/components/DashboardWizardDuration";
import { PeriodOverPeriodWrapper } from "app/components/duration-selector/period-over-period/PeriodOverPeriodWrapper";
import { SubDomainsFilter } from "app/components/filters-bar/subdomains-filter/SubDomainsFilter";
import * as utils from "app/components/filters-bar/utils";
import { FiltersEnum } from "app/components/filters-bar/utils";
import { WebSourceFilter } from "app/components/filters-bar/websource-filter/WebSourceFilter";
import type { IFilter } from "app/components/React/FiltersBar/FiltersBar";
import { FiltersBar } from "app/components/React/FiltersBar/FiltersBar";
import { FiltersMenu } from "app/components/React/FiltersBar/FiltersMenu";
import { withUIRouter } from "app/components/React/HOCs/withUIRouter";
import { i18nFilter } from "app/filters/i18nFilter";
import { handleCTAClick, TestCases } from "app/common/utils/handleGainAccessRedirect";
import { DurationSelectorSimple } from "app/components/duration-selector/DurationSelectorSimple";
import { updateFilterConsistencyCountry } from "app/routes/handlers/filter-consistency/filterConsistencyUtils";
import { EHooksVersions } from "app/services/SwHook";
import { DurationSelectorPresetOriented } from "pro-features/components/duration-selectors/DurationSelectedPresetOriented";
import { IndicatorTypes } from "pro-features/components/duration-selectors/DurationSelectedPresetOriented/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { IUnlockModalConfigTypes } from "pro-features/components/Modals/src/UnlockModal/unlockModalConfig";
import unlockModalConfig from "pro-features/components/Modals/src/UnlockModal/unlockModalConfig";
import { hideCountryTooltip, hideWebsourceTooltip } from "../../actions/commonActions";
import { CountryFilter } from "../../components/filters-bar/country-filter/CountryFilter";
import { FiltersBase } from "../../components/filters-bar/FiltersBase";
import UnlockModal from "../../components/React/UnlockModalProvider/UnlockModalProvider";
import { CUSTOM_CATEGORY_PREFIX, UserCustomCategoryService } from "../../services/category/userCustomCategoryService";
import { CategoryServiceUtils } from "../../services/CategoryServiceUtils";
import type { ICountryObject } from "../../services/CountryService";
import { allTrackers } from "../../services/track/track";

import { V2_ROUTES } from "./routes/routes";
import { extractCategoriesFromRouteParams, getLobsFirstAvailableDate, hasLobSegments } from "./routes/routeUtils";

interface IAppStateParams {
    /* Countries */
    selectedCountry: any;
    /* Websources */
    availableWebsource: any[];
    selectedWebSource: any;
    /* Duration */
    selectedDuration: string;
    selectedComparedDuration: string;
    durationPaymentHook: boolean;
}

interface IIndustryAnalysisFiltersState extends IAppStateParams {
    keys: string;
    countryFilterDisabled: boolean;
    countryFilterAvailable: boolean;
    durationSelectorDisabled: boolean;
    durationSelectorAvailable: boolean;
    webSourceFilterAvailable: boolean;
    webSourceFilterDisabled: boolean;
    comparedDurationAllowed: boolean;
    showCountryTooltip: boolean;
    countryTooltipMessage: string;
    showWebSourceTooltip?: boolean;
    hideWebSourceTooltipDispatch?: () => void;
    hideCountryTooltipDispatch?: () => void;
    isWebSourceHookOpen: boolean;
    isSideBarOpen: boolean;
    skipFilterConsistency: boolean;
    includeSubDomains: boolean;
    isSubDomainsFilterDisabled: boolean;
    includeSubDomainsDisabledTooltip?: string;
}

const hideTooltipDelay = 10000;

class IndustryAnalysisFilters extends FiltersBase<any, IIndustryAnalysisFiltersState> {
    private stateChangeSuccessHandler;

    constructor(props) {
        super(props);

        const params = this.services.swNavigator.getParams();
        const state = this.services.swNavigator.current();
        // Treat initial load as a category change to apply custom category country
        const stateParams = this.getStateParams(params, state, true);
        const countryFilterDisabled = this.isCountryDropDownDisabled(state);
        const countryFilterAvailable = this.isCountryFilterAvailable(state);
        const durationSelectorAvailable = this.isDurationSelectorAvailable(state);
        const durationSelectorDisabled = this.isDurationSelectorDisabled(params, state);
        const webSourceFilterAvailable = this.isWebSourceFilterAvailable(state);
        const webSourceFilterDisabled = this.isWebSourceFilterDisabled(state);
        const comparedDurationAllowed = this.isComparedDurationAllowed(state, params);
        const isSubDomainsFilterDisabled = this.isSubDomainsFilterDisabled(state, params);

        this.state = {
            isSideBarOpen: false,
            keys: params.category,
            ...stateParams,
            countryFilterDisabled,
            countryFilterAvailable,
            durationSelectorDisabled,
            durationSelectorAvailable,
            webSourceFilterAvailable,
            webSourceFilterDisabled,
            comparedDurationAllowed,
            showCountryTooltip: false,
            countryTooltipMessage: i18nFilter()("notpermittedcountry.tooltip"),
            durationPaymentHook: this.services.swSettings.current.durationPaymentHook,
            isWebSourceHookOpen: false,
            skipFilterConsistency: state.skipFilterConsistency,
            includeSubDomains: params.includeSubdomains !== false,
            isSubDomainsFilterDisabled,
            includeSubDomainsDisabledTooltip: this.getSubdomainsTooltip(state, params),
        };
    }

    public componentDidMount() {
        const router = this.props.router;
        this.stateChangeSuccessHandler = router.transitionService.onSuccess({}, (transition) =>
            this.onStateChangeSuccess(null, transition.to(), transition.params()),
        );
        const { hideWebSourceTooltipDispatch, hideCountryTooltipDispatch } = this.props;
        setTimeout(() => {
            hideWebSourceTooltipDispatch && hideWebSourceTooltipDispatch();
            hideCountryTooltipDispatch && hideCountryTooltipDispatch();
        }, hideTooltipDelay);

        // Path-param routes (e.g. mapping) have the country baked into the URL path.
        // Correct it to match the custom category's assigned country after mount.
        const params = this.services.swNavigator.getParams();
        const state = this.services.swNavigator.current();
        if (!this.isV2Route(state)) {
            const countryFromCustomCategory = IndustryAnalysisFilters.getCountryFromCustomCategory(params.category);
            if (countryFromCustomCategory !== null && String(params.country) !== String(countryFromCustomCategory)) {
                if (!state.skipFilterConsistency) {
                    updateFilterConsistencyCountry(countryFromCustomCategory);
                }
                this.services.swNavigator.updateParams({
                    country: countryFromCustomCategory,
                    ignoreFilterConsistency: true,
                });
            }
        }
    }

    public componentWillUnmount() {
        this.stateChangeSuccessHandler();
    }

    public componentDidUpdate(prevProps, prevState) {
        const { hideWebSourceTooltipDispatch, hideCountryTooltipDispatch } = this.props;
        if (prevState.showCountryTooltip === true) {
            setTimeout(() => {
                hideCountryTooltipDispatch && hideCountryTooltipDispatch();
            }, hideTooltipDelay);
        }

        if (prevState.showWebSourceTooltip === true) {
            setTimeout(() => {
                hideWebSourceTooltipDispatch && hideWebSourceTooltipDispatch();
            }, hideTooltipDelay);
        }
    }

    public render() {
        return (
            <div className="react-filters-container">
                <FiltersBar filters={this.getFilters()} />
                {this.getCompactFilters().length > 0 ? (
                    <FiltersMenu
                        compactFilters={this.getCompactFilters()}
                        isSideBarOpen={this.state.isSideBarOpen}
                        toggleSidebar={this.toggleSidebar}
                        onSidebarToggle={this.onSideBarToggle}
                    />
                ) : null}
            </div>
        );
    }

    @autobind
    private getCompactFilters(): ISidebarListItem[] {
        const fixedFilters: ISidebarListItem[] = [];

        fixedFilters.push({
            listItem: (
                <SubDomainsFilter
                    label={i18nFilter()("market_analysis.include_subdomains.label")}
                    key="filter-subdomains"
                    isSelected={this.state.includeSubDomains}
                    onClick={() => null}
                    disabledTooltip={this.state.includeSubDomainsDisabledTooltip}
                    isDisabled={this.state.isSubDomainsFilterDisabled}
                />
            ),
            onSidebarListItemClick: this.state.isSubDomainsFilterDisabled
                ? undefined
                : this.onSidebarListCompactElementItemChangeProp(null, this.onSubdomainsChange),
        });

        return fixedFilters;
    }

    @autobind
    private onSubdomainsChange() {
        const subdomainsState = this.state.includeSubDomains;
        const trackValue = subdomainsState ? "main domain only" : "include subdomains";
        allTrackers.dispatchTrackEvent("toggle", "switch", `filter side bar/include subdomains/${trackValue}`);
        this.services.swNavigator.updateParams({
            includeSubdomains: !subdomainsState,
        });
    }

    @autobind
    private getFilters(): IFilter[] {
        const currentState = this.services.swNavigator.current();
        let selectedCountryId;

        if (this.isV2Route(currentState)) {
            // V2 routes: derive country from URL query params
            const currentParams = this.services.swNavigator.getParams();
            const normalizeCategory = (cat: string | string[] | undefined): string =>
                Array.isArray(cat) ? cat.sort().join(",") : cat || "";
            const categoryChanged = normalizeCategory(this.state.keys) !== normalizeCategory(currentParams.category);
            const selectedCountry = this.resolveCountryParam(currentParams, currentState, categoryChanged);
            selectedCountryId = utils.getSelectedId(selectedCountry);
        } else {
            // Path-param routes: country is always present in URL path, so resolveCountryParam
            // would always pick the URL value over the custom category country. Use state instead.
            selectedCountryId = utils.getSelectedId(this.state.selectedCountry);
        }

        const selectedWebSource = utils.getSelectedId(this.state.selectedWebSource);
        const showDurationFilter = this.state.durationSelectorAvailable;
        const showCountryFilter = this.state.countryFilterAvailable;

        const showWebSourceFilter = this.state.availableWebsource.length > 0 && this.state.webSourceFilterAvailable;
        const filters = [];

        if (showCountryFilter) {
            filters.push({
                id: "country",
                filter: this.getCountryFilterComponent(this.props.showCountryTooltip, selectedCountryId),
            });
        }

        if (showDurationFilter) {
            filters.unshift({
                id: "duration",
                filter: this.getDurationSelectorComponent(),
            });
        }

        if (showWebSourceFilter && selectedWebSource) {
            filters.push({
                id: "webSource",
                filter: this.getWebSourceFilterComponent(this.props.showWebSourceTooltip, selectedWebSource),
            });
        }
        return filters;
    }

    @autobind
    private getWebSourceFilterComponent(wrapWithTooltip = false, selectedWebSource) {
        const activeSlide: IUnlockModalConfigTypes["DeviceFilters"] = "Devices";
        const isWebSourceChanged = !!proNavigator.getParams()?.isWebSourceChanged;
        const component = (
            <div key="filter-websource">
                <WebSourceFilter
                    items={this.state.availableWebsource}
                    onChange={this.onWebSourceChange}
                    selectedIds={selectedWebSource}
                    height={60}
                    appendTo="body"
                    disabled={this.state.webSourceFilterDisabled || this.state.availableWebsource.length == 1}
                    onToggle={this.trackFilterDropDownStatus("Header/WebSource Filter")}
                    presentNotificationIndicator={isWebSourceChanged}
                    presentNotificationTooltipKey={"website.analysis.present.webSource.notification.tooltip"}
                    itemWrapper={(props) => (
                        <div
                            onClick={() => {
                                this.setState({ isWebSourceHookOpen: true });
                                allTrackers.dispatchTrackEvent(
                                    "hook/Contact Us/Pop Up",
                                    "click",
                                    `WebSource Filter/${props.text}`,
                                );
                            }}
                        >
                            {props.children}
                        </div>
                    )}
                />
                <UnlockModal
                    isOpen={this.state.isWebSourceHookOpen}
                    onCloseClick={() => {
                        this.setState({ isWebSourceHookOpen: false });
                    }}
                    activeSlide={activeSlide}
                    location="Hook PRO/Web Category Analysis/Web Source Filter"
                    {...unlockModalConfig().DeviceFilters}
                />
            </div>
        );
        return wrapWithTooltip ? (
            <div
                className="scss-tooltip scss-tooltip--s scss-tooltip--autohiding"
                data-scss-tooltip={i18nFilter()("wa.overview.websource.tooltip")}
            >
                {component}
            </div>
        ) : (
            component
        );
    }

    @autobind
    private getCountryFilterComponent(wrapWithTooltip = false, selectedCountryId) {
        const isCountryChanged = !!proNavigator.getParams()?.isCountryChanged;
        const current = this.services.swNavigator.current();
        const componentConfig = this.services.swSettings.components[current.configId];
        const component = (
            <div key="filter-country">
                <CountryFilter
                    presentNotificationIndicator={isCountryChanged}
                    presentNotificationTooltipKey={"website.analysis.present.country.notification.tooltip"}
                    availableCountries={utils.getCountries(false, componentConfig)}
                    changeCountry={this.onCountryChange}
                    selectedCountryIds={selectedCountryId}
                    height={60}
                    appendTo="body"
                    disabled={this.state.countryFilterDisabled}
                    onToggle={this.trackFilterDropDownStatus("Header/Country Filter")}
                    itemWrapper={(props) => (
                        <div
                            onClick={async () => {
                                await handleCTAClick(EHooksVersions.V4, "WebCountry", TestCases.TRIAL_AFTER_TRIAL);
                            }}
                        >
                            {props.children}
                        </div>
                    )}
                ></CountryFilter>
            </div>
        );
        return wrapWithTooltip ? (
            <div
                className="scss-tooltip scss-tooltip--s scss-tooltip--autohiding"
                data-scss-tooltip={i18nFilter()("topsites.unsupportedCountry.tooltip")}
            >
                {component}
            </div>
        ) : (
            component
        );
    }

    private get durationPresets() {
        const current = this.services.swNavigator.current();
        const { datePickerPresets } = this.services.swSettings.components[current.configId];
        return preparePresets(datePickerPresets);
    }

    private getDurationSelectorComponent = () => {
        const { periodOverPeriodCompareType } = this.props;
        const current = this.services.swNavigator.current();
        const params = this.services.swNavigator.getParams();
        const { isDurationChanged } = params;
        const componentConfig = this.services.swSettings.components[current.configId];

        // Calculate restricted date range when overrideDatepickerPreset is active
        let { startDate, endDate } = componentConfig;

        // Check for LOB segments and use LobsFirstAvailableDate as startDate
        const categories = extractCategoriesFromRouteParams(params);
        if (categories.length > 0 && hasLobSegments(categories)) {
            // Use LobsFirstAvailableDate from config with fallback
            const lobsFirstAvailableDate =
                this.services.swSettings.components["MarketPlayers"]?.resources?.LobsFirstAvailableDate;
            if (lobsFirstAvailableDate) {
                startDate = dayjs.utc(lobsFirstAvailableDate);
            } else {
                // Fallback to getLobsFirstAvailableDate helper which has its own fallback
                startDate = getLobsFirstAvailableDate();
            }
        } else if (current.overrideDatepickerPreset) {
            const allowedPresets = current.overrideDatepickerPreset;
            if (Array.isArray(allowedPresets) && allowedPresets.length > 0) {
                // Find the maximum duration in months from allowed presets
                const monthPresets = allowedPresets
                    .filter((preset) => preset.endsWith("m"))
                    .map((preset) => parseInt(preset));

                if (monthPresets.length > 0) {
                    const maxMonths = Math.max(...monthPresets);
                    // Calculate restricted start date based on maximum allowed duration
                    // Subtract the full duration from end of current month to get the earliest selectable date
                    const restrictedStartDate = dayjs().endOf("month").subtract(maxMonths, "month").startOf("month");

                    // Always use the restricted date when overrideDatepickerPreset is set
                    startDate = restrictedStartDate;
                }
            }
        }

        const presentNotificationForDurationChanged = isDurationChanged === true;

        if (this.services.swSettings.user.isShortMonthIntervalsUser) {
            return (
                <DurationSelectorPresetOriented
                    minDate={startDate}
                    maxDate={endDate}
                    isDisabled={this.state.durationSelectorDisabled}
                    onSubmit={this.onDurationChange}
                    presets={this.durationPresets}
                    initialPreset={this.state.selectedDuration}
                    disableCalendar={current.hideCalendar}
                    presentNotificationIndicator={presentNotificationForDurationChanged}
                    presentNotificationTooltipKey={"website.analysis.present.duration.notification.tooltip"}
                    indicatorType={IndicatorTypes.DOT}
                />
            );
        }

        const durationSelector = (
            <DurationSelectorSimple
                key="filter-duration"
                minDate={startDate}
                maxDate={endDate}
                isDisabled={this.state.durationSelectorDisabled}
                presets={this.durationPresets}
                initialPreset={this.state.selectedDuration}
                initialComparedDuration={this.state.selectedComparedDuration || false}
                onSubmit={this.onDurationChange}
                componentName={current.configId}
                keys={this.state.keys}
                compareAllowed={this.state.comparedDurationAllowed}
                compareLabel={i18nFilter()("categoryanalysis.duration.compare.label")}
                compareDisabledTooltipText={i18nFilter()("categoryanalysis.duration.compare.disabled.tooltip")}
                hasPermissionsLock={this.state.durationPaymentHook}
                disableCalendar={current.hideCalendar}
                disableFooter={current.hideCalendar}
                onToggle={this.trackFilterDropDownStatus("Date range")}
                appendTo="body"
                periodOverPeriodCompareType={periodOverPeriodCompareType}
                presentNotificationIndicator={presentNotificationForDurationChanged}
                presentNotificationTooltipKey={"website.analysis.present.duration.notification.tooltip"}
            />
        );

        if (!this.state.comparedDurationAllowed) {
            return durationSelector;
        }

        return (
            <PeriodOverPeriodWrapper.Container>
                {durationSelector}
                <PeriodOverPeriodWrapper.Delimiter />
                <PeriodOverPeriodWrapper.Toggle />
            </PeriodOverPeriodWrapper.Container>
        );
    };

    @autobind
    private async onCountryChange(country) {
        const currentCountry = this.services.swNavigator.getParams().country;
        if (String(currentCountry) === String(country.id)) {
            return;
        }
        this.trackFilterValue(`Header/Country Filter/${country.text}`);
        if (!this.state.skipFilterConsistency) {
            updateFilterConsistencyCountry(country.id);
        }

        const isV2 = this.isV2Route(this.services.swNavigator.current());

        this.services.swNavigator.updateParams({
            country: country.id,
            isCountryChanged: null,
            // Bypass redirectTo filter consistency for path-param routes so it doesn't
            // override the user's explicit selection during the full transition.
            ...(!isV2 && { ignoreFilterConsistency: true }),
        });

        if (isV2) {
            this.forceUpdate();
        } else {
            this.setState({ selectedCountry: country });
        }
    }

    @autobind
    private onWebSourceChange(webSource) {
        const currentWebSource = this.services.swNavigator.getParams().webSource;
        if (currentWebSource === webSource.id) {
            return;
        }
        this.trackFilterValue(`Header/WebSource Filter/${webSource.text}`);
        if (!this.state.skipFilterConsistency) {
            this.props.webSourceSelected(webSource.id);
        }
        this.services.swNavigator.updateQueryParams({
            webSource: webSource.id,
        });
    }

    @autobind
    private onDurationChange(duration, comparedDuration, durationTypeForTracking = null) {
        this.services.track.all.trackEvent(
            "Drop Down",
            "click",
            `Date range/${duration}/${durationTypeForTracking}`,
            duration,
        );
        if (!this.state.skipFilterConsistency) {
            this.props.durationSelected(duration);
        }
        this.services.swNavigator.updateParams({
            duration,
            comparedDuration: comparedDuration ? comparedDuration : "",
            isDurationChanged: null,
        });
    }

    @autobind
    private getWebSources(state, params) {
        return utils.getAvailableWebSource(state, params);
    }

    @autobind
    private isCorrectState(toState) {
        return this.services.swNavigator.isIndustryAnalysis(toState);
    }

    private isV2Route(state): boolean {
        return Object.values(V2_ROUTES).includes(state.name);
    }

    @autobind
    private onStateChangeSuccess(evt, toState, toParams) {
        if (!this.isCorrectState(toState)) {
            return;
        }

        // V2: getParams() is more reliable (query param updates may not be in transition.params()).
        // Path-param routes: toParams from the completed transition is the reliable source.
        const isV2 = this.isV2Route(toState);
        const currentParams = isV2 ? this.services.swNavigator.getParams() : toParams;

        const currentCategory = this.state.keys;
        const newCategory = currentParams.category;

        const normalizeCategory = (cat: string | string[] | undefined): string =>
            Array.isArray(cat) ? cat.sort().join(",") : cat || "";
        const categoryChanged = normalizeCategory(currentCategory) !== normalizeCategory(newCategory);

        // When switching categories, always reset to the category's predefined country if one exists
        if (categoryChanged) {
            const countryFromCustomCategory = IndustryAnalysisFilters.getCountryFromCustomCategory(
                currentParams.category,
            );
            if (
                countryFromCustomCategory !== null &&
                String(currentParams.country) !== String(countryFromCustomCategory)
            ) {
                this.setState({ keys: currentParams.category });
                if (!toState.skipFilterConsistency) {
                    updateFilterConsistencyCountry(countryFromCustomCategory);
                }
                this.services.swNavigator.updateParams({
                    country: countryFromCustomCategory,
                    ignoreFilterConsistency: true,
                });
                return;
            }
        }

        const stateParams = this.getStateParams(currentParams, toState, categoryChanged);
        const countryFilterDisabled = this.isCountryDropDownDisabled(toState);
        const countryFilterAvailable = this.isCountryFilterAvailable(toState);
        const durationSelectorAvailable = this.isDurationSelectorAvailable(toState);
        const durationSelectorDisabled = this.isDurationSelectorDisabled(currentParams, toState);
        const webSourceFilterAvailable = this.isWebSourceFilterAvailable(toState);
        const webSourceFilterDisabled = this.isWebSourceFilterDisabled(toState);
        const comparedDurationAllowed = this.isComparedDurationAllowed(toState, currentParams);
        const isSubDomainsFilterDisabled = this.isSubDomainsFilterDisabled(toState, currentParams);

        this.setState({
            ...stateParams,
            keys: currentParams.category,
            countryFilterDisabled,
            countryFilterAvailable,
            durationSelectorDisabled,
            durationSelectorAvailable,
            webSourceFilterAvailable,
            webSourceFilterDisabled,
            comparedDurationAllowed,
            durationPaymentHook: this.services.swSettings.current.durationPaymentHook,
            skipFilterConsistency: toState.skipFilterConsistency,
            includeSubDomains: currentParams.includeSubdomains !== false,
            isSubDomainsFilterDisabled,
            includeSubDomainsDisabledTooltip: this.getSubdomainsTooltip(toState, currentParams),
        });
    }

    private isCountryDropDownDisabled(state) {
        return state.data?.filtersConfig?.country === FiltersEnum.DISABLED;
    }

    private isCountryFilterAvailable(state) {
        return state.data?.filtersConfig?.country !== FiltersEnum.HIDDEN;
    }

    private isDurationSelectorDisabled(params, state) {
        if (typeof state.data.filtersConfig === "function") {
            const result = state.data.filtersConfig(params);
            return result.duration === FiltersEnum.DISABLED;
        }
        return state.data?.filtersConfig?.duration === FiltersEnum.DISABLED;
    }

    private isDurationSelectorAvailable(state) {
        return state.data?.filtersConfig?.duration !== FiltersEnum.HIDDEN;
    }

    private isWebSourceFilterDisabled(state) {
        return state.data?.filtersConfig?.webSource === FiltersEnum.DISABLED;
    }

    private isWebSourceFilterAvailable(state) {
        return state.data?.filtersConfig?.webSource !== FiltersEnum.HIDDEN;
    }

    private isComparedDurationAllowed(state, params) {
        // Check for LOB segments - period-over-period not allowed with LOBs
        const categoryParam = params.category;
        const categories: string[] = categoryParam
            ? Array.isArray(categoryParam)
                ? categoryParam
                : [categoryParam]
            : [];
        const hasLobInCategory = categories.length > 0 && hasLobSegments(categories);

        if (hasLobInCategory) {
            return false;
        }
        return state.periodOverPeriodEnabled === true;
    }

    private isSubDomainsFilterDisabled(state, params) {
        const isOverviewPage = state.name === V2_ROUTES.overview;
        const categories = Array.isArray(params.category) ? params.category : [params.category];
        const hasPreDefinedIndustry = categories.some(
            (category) => !UserCustomCategoryService.isCustomCategory(category),
        );
        return !isOverviewPage || hasPreDefinedIndustry;
    }

    private selectedDuration(state, params) {
        if (typeof state.overrideParams === "function") {
            return state.overrideParams(params).duration;
        } else {
            return params.duration;
        }
    }

    private static getCountryFromCustomCategory(categoryParam: string | Array<string>): number | null {
        if (!categoryParam) {
            return null;
        }

        try {
            const categories = Array.isArray(categoryParam) ? categoryParam : [categoryParam];
            const firstCategory = categories[0];

            const categoryId = firstCategory?.startsWith(CUSTOM_CATEGORY_PREFIX)
                ? UserCustomCategoryService.removeCategoryIdPrefix(firstCategory)
                : firstCategory;

            const customCategory = UserCustomCategoryService.getCustomCategoryById(categoryId);
            if (customCategory) {
                const industryCountry = CategoryServiceUtils.getIndustryCountry(customCategory);
                if (industryCountry) {
                    return industryCountry.id;
                }
            }
        } catch (error: unknown) {
            const e = error instanceof Error ? error : new Error("Unknown error");
            Sentry.error("Error getting country from custom category", e, {
                extra: { categoryParam },
            });
        }

        return null;
    }

    private resolveCountryParam(params, state, categoryChanged = false): ICountryObject | undefined {
        // Use the same componentConfig as the CountryFilter dropdown to ensure consistency
        const componentConfig = this.services.swSettings.components[state.configId] || this.services.swSettings.current;
        const availableCountries = utils.getCountries(false, componentConfig);
        const countryFromCustomCategory = IndustryAnalysisFilters.getCountryFromCustomCategory(params.category);

        let countryParam: string | number | undefined;

        if (categoryChanged && countryFromCustomCategory !== null) {
            countryParam = countryFromCustomCategory;
        } else if (params.country !== undefined) {
            countryParam = params.country;
        } else if (countryFromCustomCategory !== null) {
            countryParam = countryFromCustomCategory;
        } else if (typeof state.overrideParams === "function") {
            countryParam = state.overrideParams(params).country;
        } else {
            return undefined;
        }

        return _.find<ICountryObject>(availableCountries, {
            id: typeof countryParam === "number" ? countryParam : parseInt(countryParam),
        });
    }

    private getStateParams(params, state, categoryChanged = false): IAppStateParams {
        const availableWebsource = this.getWebSources(state, params);

        const selectedCountry = this.resolveCountryParam(params, state, categoryChanged);

        const webSourceParam =
            params.webSource !== undefined
                ? params.webSource
                : typeof state.overrideParams === "function"
                  ? state.overrideParams(params).webSource
                  : undefined;
        const selectedWebSource = _.find(availableWebsource, { id: webSourceParam });
        const selectedDuration = this.selectedDuration(state, params);
        const selectedComparedDuration = params.comparedDuration;

        return {
            availableWebsource,
            selectedCountry,
            selectedWebSource,
            selectedDuration,
            selectedComparedDuration,
            durationPaymentHook: this.services.swSettings.current.durationPaymentHook,
        };
    }

    @autobind
    private onDurationCompactClick() {
        allTrackers.dispatchTrackEvent("Drop Down", "open", `Date Range`);
    }

    @autobind
    private onSidebarListCompactElementItemChangeProp(item, cb) {
        return async () => {
            await this.setStateAsync({
                isSideBarOpen: false,
            });
            setTimeout(() => {
                cb(item);
            }, 400);
        };
    }

    private getSubdomainsTooltip(state, params): string {
        const isOverviewPage = state.name === V2_ROUTES.overview;
        const categories = Array.isArray(params.category) ? params.category : [params.category];
        const hasPreDefinedIndustry = categories.some(
            (category) => !UserCustomCategoryService.isCustomCategory(category),
        );

        if (!isOverviewPage) {
            return i18nFilter()("market_analysis.include_subdomains.tooltip.disabled_for_page");
        }
        if (hasPreDefinedIndustry) {
            return i18nFilter()("market_analysis.include_subdomains.tooltip.disabled_for_industry");
        }
        return i18nFilter()("market_analysis.include_subdomains.tooltip.enabled");
    }
}

function mapDispatchToProps(dispatch) {
    return {
        hideWebSourceTooltipDispatch: () => {
            dispatch(hideWebsourceTooltip());
        },
        hideCountryTooltipDispatch: () => {
            dispatch(hideCountryTooltip());
        },
        durationSelected: (duration) => {
            dispatch(durationFilterDSSelected(duration));
        },
        webSourceSelected: (webSource) => {
            dispatch(webSourceFilterDSSelected(webSource));
        },
    };
}

function mapStateToProps(state) {
    const {
        common: { showWebSourceTooltip, showCountryTooltip },
        WebsiteAnalysisFilters,
    } = state;
    const { periodOverPeriodCompareType } = WebsiteAnalysisFilters;
    return {
        showWebSourceTooltip,
        showCountryTooltip,
        periodOverPeriodCompareType,
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withUIRouter(IndustryAnalysisFilters));
